/**
 * Once the page has started a full page load (a form submission, a location
 * change), nothing may move it again until the browser replaces it.
 *
 * While that load is pending Safari fails every new load: a lazy route's code
 * can't be imported, React Router reloads the page when that happens, and the
 * reload abandons the pending load. That kept users signed in after Sign out,
 * and looped a signed-out visit to a workspace URL between the workspace and
 * the sign-in redirect forever (NG-2513). Chrome lets the pending load finish,
 * so the bug only shows in Safari.
 *
 * So a full page load that can race the app's own navigation goes through here:
 * the auth forms' submissions, the API's sign-in redirect and the auth guard's.
 * Once the page is leaving, `leavePage` does nothing more and the API client
 * settles no request, so nothing reacts by navigating. A one-off navigation a
 * user starts with a click (an OAuth hop, a reload button) has no such race.
 *
 * React Router itself is the one caller we can't route through here: during
 * start-up it imports the matched routes' code alongside the loaders, so a load
 * that leaves early makes those imports fail, and it reloads unconditionally.
 * patches/react-router@*.patch makes it wait instead while the page is leaving.
 */
declare global {
  interface Window {
    /** Read by our patch to React Router's route-module loader
     *  (patches/react-router@*.patch): set, a failed import waits for the page
     *  to be replaced instead of reloading it. Keep the name in sync. */
    __cognotaPageLeaving?: boolean;
  }
}

/** True once a full page load is under way. */
export function isPageLeaving(): boolean {
  return window.__cognotaPageLeaving === true;
}

/**
 * Record that a full page load is under way, for a caller that starts it
 * itself (a form submission). If Back later restores this page from the
 * back-forward cache, it reloads: the page was left as it was, so it would
 * otherwise show stale, possibly signed-out, state.
 */
export function markPageLeaving(): void {
  if (isPageLeaving()) return;
  window.__cognotaPageLeaving = true;
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) window.location.reload();
  });
}

/**
 * Start a full page load to `url`, unless one is already under way, which wins.
 * Never settles: the caller is about to be replaced, so nothing may run after it.
 */
export function leavePage(url: string): Promise<never> {
  if (!isPageLeaving()) {
    markPageLeaving();
    window.location.href = url;
  }
  return new Promise<never>(() => {});
}
